import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { SITE } from "@/lib/site";
import logo from "@/assets/logo.webp";

const servicesMenu = [
  { to: "/home-watch-services", label: "Home Watch" },
  { to: "/junk-removal", label: "Junk Removal" },
  { to: "/estate-cleanouts", label: "Estate Cleanouts" },
  { to: "/property-cleanouts", label: "Property Cleanouts" },
  { to: "/garage-cleanouts", label: "Garage Cleanouts" },
  { to: "/foreclosure-cleanouts", label: "Foreclosure Cleanouts" },
  { to: "/eviction-cleanouts", label: "Eviction Cleanouts" },
  { to: "/storm-debris-removal", label: "Storm Debris Removal" },
  { to: "/furniture-removal", label: "Furniture Removal" },
  { to: "/appliance-removal", label: "Appliance Removal" },
  { to: "/concierge-services", label: "Concierge Services" },
] as const;

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/90 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <img src={logo} alt="" width={64} height={64} className="h-9 w-9" />
          <div className="leading-tight">
            <div className="font-serif text-lg text-primary">{SITE.name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Complete Property Care</div>
          </div>
        </Link>
        <nav className="hidden items-center gap-7 lg:flex">
          <Link
            to="/"
            className="text-sm text-foreground/80 transition-colors hover:text-primary"
            activeProps={{ className: "text-primary font-medium" }}
            activeOptions={{ exact: true }}
          >
            Home
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-foreground/80 transition-colors hover:text-primary"
              onClick={() => setServicesOpen((v) => !v)}
              aria-expanded={servicesOpen}
              aria-haspopup="true"
            >
              Services <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {servicesOpen && (
              <div className="absolute left-0 top-full min-w-[240px] rounded-md border border-border bg-background py-2 shadow-lg">
                {servicesMenu.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    onClick={() => setServicesOpen(false)}
                    className="block px-4 py-2 text-sm text-foreground/80 hover:bg-secondary hover:text-primary"
                    activeProps={{ className: "text-primary font-medium bg-secondary" }}
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/service-areas"
            className="text-sm text-foreground/80 transition-colors hover:text-primary"
            activeProps={{ className: "text-primary font-medium" }}
          >
            Service Areas
          </Link>
          <Link
            to="/about"
            className="text-sm text-foreground/80 transition-colors hover:text-primary"
            activeProps={{ className: "text-primary font-medium" }}
          >
            About
          </Link>
          <Link
            to="/contact"
            className="text-sm text-foreground/80 transition-colors hover:text-primary"
            activeProps={{ className: "text-primary font-medium" }}
          >
            Contact
          </Link>
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <a href={SITE.phoneHref} className="flex items-center gap-1.5 text-sm font-medium text-primary">
            <Phone className="h-4 w-4" /> {SITE.phone}
          </a>
          <Link
            to="/contact"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            Get a Free Quote
          </Link>
        </div>
        <button
          className="rounded-md p-2 lg:hidden"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-border bg-background lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="rounded-md px-2 py-2.5 text-sm text-foreground/80 hover:bg-secondary"
              activeProps={{ className: "text-primary font-medium bg-secondary" }}
              activeOptions={{ exact: true }}
            >
              Home
            </Link>
            <button
              type="button"
              onClick={() => setMobileServicesOpen((v) => !v)}
              className="flex items-center justify-between rounded-md px-2 py-2.5 text-left text-sm text-foreground/80 hover:bg-secondary"
              aria-expanded={mobileServicesOpen}
            >
              Services
              <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileServicesOpen && (
              <div className="ml-3 flex flex-col gap-1 border-l border-border pl-3">
                {servicesMenu.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    onClick={() => setOpen(false)}
                    className="rounded-md px-2 py-2 text-sm text-foreground/80 hover:bg-secondary"
                    activeProps={{ className: "text-primary font-medium bg-secondary" }}
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            )}
            {[
              { to: "/service-areas", label: "Service Areas" },
              { to: "/about", label: "About" },
              { to: "/contact", label: "Contact" },
            ].map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-2 py-2.5 text-sm text-foreground/80 hover:bg-secondary"
                activeProps={{ className: "text-primary font-medium bg-secondary" }}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-md bg-primary px-4 py-2.5 text-center text-sm font-medium text-primary-foreground"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}